import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  Award,
  CheckCircle,
  FileText,
  Star,
  Edit3,
  AlertTriangle,
  AlertCircle,
  Upload,
  Trophy,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import VerifiedBadge from "../../components/VerifiedBadge";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import Tooltip from "../../components/Tooltip/JobSeekerTooltip";
import { safeToDate } from "../../utils/SafeToDate";

// Gamification Components
import LevelProgressBar from "../../components/Gamification/LevelProgressBar";
import StreakCounter from "../../components/Gamification/StreakCounter";
import Leaderboard from "../../components/Gamification/Leaderboard";
import AchievementGrid from "../../components/Gamification/AchievementGrid";
import AchievementModal from "../../components/Gamification/AchievementModal";

// Gamification Hooks
import { useUserProgress, useUserAchievements, useActivityTracker } from "../../hooks/useGamification";
import { Achievement } from "../../constants/gamification";
import { Brain, Flame } from "lucide-react";

// Profile Data Interface
interface ProfileData {
  name: string;
  email: string;
  phone: string;
  location: string;
  joinedDate: string;
  experience: string;
  title: string;
  bio: string;
  skills: string[];
  education: string;
  resumeUploaded: boolean;
  resumeData: any; // Can be further typed if needed
}

// Edit Form Data Interface
interface EditFormData {
  name: string;
  email: string;
  phone: string;
  location: string;
  bio: string;
  title: string;
}

// Test Result Interface
interface TestResult {
  id: string;
  jobTitle?: string;
  completedDate: string;
  score: number;
  [key: string]: any;
}

const Profile: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [recentTests, setRecentTests] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    title: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const { isVerified, verificationScore, verificationDate, loading: verificationLoading } = useVerificationStatus();
  const [stats, setStats] = useState({
    testsCompleted: 0,
    interviewsDone: 0,
    successRate: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Gamification State
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [showAllAchievements, setShowAllAchievements] = useState(false);

  // Gamification Hooks
  const { progress: userProgress, loading: progressLoading } = useUserProgress(user?.id || '');
  const { achievements: userAchievements, loading: achievementsLoading } = useUserAchievements(user?.id || '');
  const { trackActivity } = useActivityTracker(user?.id || '');


  // Extract the profile loading logic into a separate function for reuse
  const loadProfileDataFromFirebase = async () => {
    if (!user) return;

    try {
      const profileDoc = await getDoc(doc(db, "userProfiles", user.id));

      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        const resumeData = profileData.resumeData;

        // Combine user data with resume data
        const combinedData = {
          name: resumeData?.personalInfo?.name || resumeData?.name || user?.name || "User",
          email: resumeData?.personalInfo?.email || resumeData?.email || user?.email || "",
          phone: resumeData?.personalInfo?.phone || resumeData?.phone || "+1 (555) 123-4567",
          location: resumeData?.personalInfo?.location || resumeData?.location || "Location not provided",
          joinedDate:
            profileData.joinedDate ||
            safeToDate(profileData.createdAt)?.toLocaleDateString('en-CA') ||
            safeToDate(user?.createdAt)?.toLocaleDateString('en-CA') ||
            new Date().toLocaleDateString('en-CA'),
          experience: resumeData?.professionalInfo?.experience || resumeData?.experience || "Not specified",
          title: resumeData?.workHistory?.[0]?.title || resumeData?.professionalInfo?.experience || "Professional",
          bio: resumeData?.professionalInfo?.summary || resumeData?.summary || "Professional summary not available.",
          skills: resumeData?.professionalInfo?.skills || resumeData?.skills || [],
          education: resumeData?.professionalInfo?.education || resumeData?.education || "Education not provided",
          resumeUploaded: profileData.resumeUploaded || false,
          resumeData: resumeData,
        };

        setProfileData(combinedData);
      } else {
        // No profile data found, use default data
        const defaultData = {
          name: user?.name || "User",
          email: user?.email || "",
          phone: "+1 (555) 123-4567",
          location: "Location not provided",
          joinedDate:
            safeToDate(user?.createdAt)?.toLocaleDateString('en-CA') ||
            new Date().toLocaleDateString('en-CA'),
          experience: "Not specified",
          title: "Professional",
          bio: "Please upload your resume to populate your profile information.",
          skills: [],
          education: "Education not provided",
          resumeUploaded: false,
          resumeData: null,
        };

        setProfileData(defaultData);
      }
    } catch (error) {
      throw error;
    }
  };

  // Load profile data from Firebase
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;

      try {
        await loadProfileDataFromFirebase();
      } catch (error) {
        // Fallback to default data
        const defaultData = {
          name: user?.name || "User",
          email: user?.email || "",
          phone: "+1 (555) 123-4567",
          location: "Location not provided",
          joinedDate: "2024-12-15",
          experience: "Not specified",
          title: "Professional",
          bio: "Please upload your resume to populate your profile information.",
          skills: [],
          education: "Education not provided",
          resumeUploaded: false,
          resumeData: null,
        };

        setProfileData(defaultData);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [user, db]);

  // Fetch recent test results
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id),
      orderBy("completedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          jobTitle: data.jobTitle,
          completedDate:
            safeToDate(data.completedAt)?.toISOString()?.split("T")[0] ||
            new Date().toISOString().split("T")[0],
          score: data.totalScore || data.score || 0,
          ...data,
        } as TestResult;
      });
      setRecentTests(results.slice(0, 5)); // Show only recent 5 tests

      // Calculate stats
      let totalScore = 0;
      let validScores = 0;
      let interviewsCount = 0;

      results.forEach(test => {
        const testScore = test.totalScore || test.score || test.interviewScore;
        if (testScore !== undefined && testScore !== null) {
          totalScore += testScore;
          validScores++;
        }
        if (test.interviewScore !== undefined && test.interviewScore !== null) {
          interviewsCount++;
        }
      });

      const avgSuccessRate = validScores > 0 ? Math.round(totalScore / validScores) : 0;

      setStats({
        testsCompleted: results.length,
        interviewsDone: interviewsCount,
        successRate: avgSuccessRate
      });
      setLoadingStats(false);
    });

    return () => unsubscribe();
  }, [user, db]);

  const handleValidateSkills = () => {
    if (!profileData?.resumeData) {
      alert("Please upload a resume first to validate your skills.");
      return;
    }

    // Navigate to the new skills validation page
    navigate("/job-seeker/skills-validation");
  };


  const handleEditClick = () => {
    setEditFormData({
      name: profileData?.name || '',
      email: profileData?.email || '',
      phone: profileData?.phone || '',
      location: profileData?.location || '',
      bio: profileData?.bio || '',
      title: profileData?.title || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      name: '',
      email: '',
      phone: '',
      location: '',
      bio: '',
      title: '',
    });
  };

  const handleSaveProfile = async () => {
    if (!user || !editFormData.name.trim()) {
      alert('Name is required');
      return;
    }

    setIsSaving(true);

    try {
      const profileRef = doc(db, 'userProfiles', user.id);
      
      // First, check if the profile document exists
      const profileSnapshot = await getDoc(profileRef);
      
      let updateData;
      
      if (profileSnapshot.exists()) {
        // Document exists, update it
        const existingData = profileSnapshot.data();
        
        // Safely update nested resume data structure
        const updatedResumeData = {
          ...existingData.resumeData,
          personalInfo: {
            ...existingData.resumeData?.personalInfo,
            name: editFormData.name,
            email: editFormData.email,
            phone: editFormData.phone,
            location: editFormData.location,
          },
          professionalInfo: {
            ...existingData.resumeData?.professionalInfo,
            summary: editFormData.bio,
          },
          // Keep existing work history and other data
          workHistory: existingData.resumeData?.workHistory || [],
          skills: existingData.resumeData?.skills || [],
          education: existingData.resumeData?.education || '',
        };

        updateData = {
          ...existingData,
          resumeData: updatedResumeData,
          lastUpdated: new Date(),
        };

        await updateDoc(profileRef, updateData);
      } else {
        // Document doesn't exist, create a new one
        updateData = {
          userId: user.id,
          resumeData: {
            personalInfo: {
              name: editFormData.name,
              email: editFormData.email,
              phone: editFormData.phone,
              location: editFormData.location,
            },
            professionalInfo: {
              summary: editFormData.bio,
              skills: [],
              experience: editFormData.title || 'Professional',
            },
            workHistory: [],
            education: 'Education not provided',
          },
          resumeUploaded: false,
          createdAt: new Date(),
          lastUpdated: new Date(),
        };

        await setDoc(profileRef, updateData);
      }

      // Update local state immediately for UI responsiveness
      setProfileData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          name: editFormData.name,
          email: editFormData.email,
          phone: editFormData.phone,
          location: editFormData.location,
          bio: editFormData.bio,
          title: editFormData.title,
        };
      });

      setIsEditing(false);
      setEditFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        bio: '',
        title: '',
      });

      // Update the Auth context so the Header reflects the new name
      updateUserProfile({ name: editFormData.name });

      // Show success message
      alert('Profile updated successfully!');

      // Track profile edit activity
      trackActivity('profile_edit');

      // Reload profile data from Firebase to ensure consistency
      await loadProfileDataFromFirebase();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      alert(`Failed to update profile: ${errorMessage}. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header - Glassmorphism Hero */}
      <div className="relative overflow-hidden rounded-glass p-10 shadow-2xl" style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
      }}>
        {/* Decorative orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl"></div>

        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
              <UserIcon className="w-12 h-12 text-white" />
            </div>
            <div>
              {isEditing ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    className="text-3xl font-bold bg-white/20 text-white placeholder-white/60 border border-white/30 rounded-lg px-3 py-2 w-full max-w-md focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    placeholder="Enter your name"
                  />
                  <input
                    type="text"
                    name="title"
                    value={editFormData.title}
                    onChange={handleInputChange}
                    className="text-lg bg-white/20 text-red-100 placeholder-white/60 border border-white/30 rounded-lg px-3 py-2 w-full max-w-md focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    placeholder="Enter your job title"
                  />
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4 text-red-100" />
                      <input
                        type="text"
                        name="location"
                        value={editFormData.location}
                        onChange={handleInputChange}
                        className="bg-white/20 text-red-100 placeholder-white/60 border border-white/30 rounded px-2 py-1 w-40 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        placeholder="Location"
                      />
                    </div>
                    <div className="flex items-center space-x-1 text-red-100">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {profileData?.joinedDate}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-2">
                    <h1 className="text-3xl font-bold">{profileData?.name}</h1>
                    <VerifiedBadge 
                      isVerified={isVerified} 
                      size="lg" 
                      className="text-white" 
                    />
                  </div>
                  <p className="text-red-100 text-lg">{profileData?.title}</p>
                  {isVerified && verificationScore && (
                    <div className="text-red-100 text-sm">
                      Skills verified with {verificationScore}% score
                    </div>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-red-100">
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{profileData?.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {profileData?.joinedDate}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {isEditing ? (
            <div className="flex space-x-2">
              <Tooltip content="Save your profile changes">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="btn-primary disabled:opacity-50 relative"
                >
                  {isSaving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Save
                </button>
              </Tooltip>
              <Tooltip content="Cancel editing and discard changes">
                <button
                  onClick={handleCancelEdit}
                  className="btn-secondary relative"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              </Tooltip>
            </div>
          ) : (
            <Tooltip content="Edit your profile information">
              <button
                onClick={handleEditClick}
                className="btn-secondary relative"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Gamification Row: Level Progress + Streak */}
      {!progressLoading && userProgress && (
        <div className="grid md:grid-cols-2 gap-6">
          <LevelProgressBar
            currentLevel={userProgress.currentLevel}
            currentXP={userProgress.totalXP}
            levelName={userProgress.currentLevelName || 'Beginner'}
          />
          <StreakCounter
            currentStreak={userProgress.currentStreak || 0}
            longestStreak={userProgress.longestStreak || 0}
            lastActivityDate={userProgress.lastActivityDate}
            freezeTokens={userProgress.streakFreezeTokens || 0}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* About Section */}
          <div className="glass-card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
            {isEditing ? (
              <textarea
                name="bio"
                value={editFormData.bio}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                placeholder="Tell us about yourself, your experience, and your goals..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">{profileData?.bio}</p>
            )}
          </div>

          {/* Stats Overview */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Tests Completed</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {loadingStats ? '-' : stats.testsCompleted}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Interviews Done</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {loadingStats ? '-' : stats.interviewsDone}
                  </p>
                </div>
                <Brain className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {loadingStats ? '-' : `${stats.successRate}%`}
                  </p>
                </div>
                <Trophy className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Skills Verification Score Card */}
          {verificationScore !== undefined && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-900">Skills Verification Test</h3>
                  </div>
                  <p className="text-gray-700 mb-3">
                    {isVerified
                      ? "Congratulations! You've passed the skills verification test."
                      : "You've completed the skills test. Keep practicing to reach 60% and get verified!"}
                  </p>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Your Score:</span>
                      <span className={`text-3xl font-bold ${isVerified ? 'text-green-600' : 'text-orange-600'}`}>
                        {verificationScore}%
                      </span>
                    </div>
                    {verificationDate && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Date:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(verificationDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="hidden md:block">
                  {isVerified ? (
                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center">
                      <Star className="w-12 h-12 text-orange-600" />
                    </div>
                  )}
                </div>
              </div>
              {!isVerified && (
                <div className="mt-4">
                  <Link
                    to="/job-seeker/skills-validation"
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-semibold"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Retake Test to Get Verified
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Leaderboard Button */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trophy className="w-7 h-7 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                  Leaderboard
                </h2>
                <p className="text-gray-600">
                  See where you rank among top performers
                </p>
              </div>
              <button
                onClick={() => setShowFullLeaderboard(true)}
                className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center space-x-2"
              >
                <Trophy className="w-4 h-4" />
                <span>View Rankings</span>
              </button>
            </div>
          </div>

          {/* Achievements and Profile Stats Side by Side */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Achievements */}
            <div className="glass-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                  <Award className="w-5 h-5 text-yellow-600" />
                  <span>Achievements</span>
                </h3>
                <button
                  onClick={() => setShowAllAchievements(!showAllAchievements)}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  {showAllAchievements ? 'Show Less' : 'View All →'}
                </button>
              </div>

              {!achievementsLoading && userAchievements.length > 0 ? (
                <div className="space-y-3">
                  {userAchievements.slice(0, 3).map((achievement) => (
                    <div
                      key={achievement.achievementId}
                      className="flex items-center space-x-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200"
                    >
                      <span className="text-3xl">{achievement.emoji}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">{achievement.title}</p>
                        <p className="text-xs text-gray-600">{achievement.points} points</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  ))}
                  <div className="text-center pt-2">
                    <p className="text-sm text-gray-600">
                      {userAchievements.length} / 25+ achievements unlocked
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">Complete tests to unlock achievements!</p>
                </div>
              )}
            </div>

            {/* Profile Stats */}
            <div className="glass-card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Profile Stats
              </h2>
              {(() => {
                // Calculate profile completion dynamically
                const completionItems = [
                  { label: 'Name', completed: !!(profileData?.name && profileData.name !== 'User'), weight: 15 },
                  { label: 'Email', completed: !!profileData?.email, weight: 15 },
                  { label: 'Phone', completed: !!(profileData?.phone && profileData.phone !== '+1 (555) 123-4567'), weight: 10 },
                  { label: 'Location', completed: !!(profileData?.location && profileData.location !== 'Location not provided'), weight: 10 },
                  { label: 'Resume Uploaded', completed: !!profileData?.resumeUploaded, weight: 20 },
                  { label: 'Skills Added', completed: !!(profileData?.skills && profileData.skills.length > 0), weight: 15 },
                  { label: 'Skills Verified', completed: !!isVerified, weight: 15 },
                ];
                const completedWeight = completionItems.reduce((sum, item) => sum + (item.completed ? item.weight : 0), 0);
                const totalWeight = completionItems.reduce((sum, item) => sum + item.weight, 0);
                const completionPercentage = Math.round((completedWeight / totalWeight) * 100);

                return (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Profile Completion</span>
                      <span className={`font-medium ${completionPercentage >= 80 ? 'text-green-600' : completionPercentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {completionPercentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${completionPercentage >= 80 ? 'bg-green-500' : completionPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${completionPercentage}%` }}
                      ></div>
                    </div>

                    {/* Completion Breakdown */}
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Completion breakdown:</p>
                      <div className="space-y-1">
                        {completionItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span className={item.completed ? 'text-gray-700' : 'text-gray-400'}>
                              {item.label}
                            </span>
                            <span className={`flex items-center ${item.completed ? 'text-green-600' : 'text-gray-400'}`}>
                              {item.completed ? (
                                <CheckCircle className="w-3 h-3 mr-1" />
                              ) : (
                                <AlertCircle className="w-3 h-3 mr-1" />
                              )}
                              {item.weight}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-gray-600">Tests Completed</span>
                      <span className="text-blue-600 font-medium">
                        {recentTests.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Average Score</span>
                      <span className="text-green-600 font-medium">
                        {recentTests.length > 0
                          ? Math.round(
                              recentTests.reduce(
                                (sum, test) => sum + (test.score || 0),
                                0
                              ) / recentTests.length
                            )
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Pro Tips Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-blue-600 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900">Pro Tips for Leveling Up</h3>
                <ul className="mt-3 space-y-2 text-sm text-blue-800">
                  <li>• Complete tests daily to maintain your streak and earn bonus points</li>
                  <li>• Aim for high scores to unlock performance-based achievements</li>
                  <li>• Keep your profile updated to unlock profile achievements</li>
                  <li>• Compete on the leaderboard to reach the top ranks</li>
                  <li>• Check back daily - new challenges and achievements coming soon!</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Work Experience */}
          {profileData?.resumeData?.workHistory && (
            <div className="glass-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Work Experience
                </h2>
                <Briefcase className="w-5 h-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                {profileData.resumeData.workHistory.map(
                  (job: any, index: number) => (
                    <div key={index} className="border-l-2 border-red-200 pl-4">
                      <h3 className="font-semibold text-gray-900">
                        {job.title}
                      </h3>
                      <p className="text-red-600 font-medium">{job.company}</p>
                      <p className="text-sm text-gray-500 mb-2">
                        {job.duration}
                      </p>
                      <p className="text-gray-700">{job.description}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="glass-card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Contact Information
            </h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Email address"
                  />
                ) : (
                  <span className="text-gray-700">{profileData?.email}</span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Phone number"
                  />
                ) : (
                  <span className="text-gray-700">{profileData?.phone}</span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="text"
                    name="location"
                    value={editFormData.location}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Location"
                  />
                ) : (
                  <span className="text-gray-700">{profileData?.location}</span>
                )}
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="glass-card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {profileData?.skills?.map((skill: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Resume Section */}
          <div className="glass-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Resume Data
              </h2>
              {profileData?.resumeUploaded ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
            </div>

            {profileData?.resumeUploaded ? (
              <>
                {isVerified ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-900 font-medium">
                        Your Verified Skills
                      </span>
                    </div>
                    <p className="text-green-800 text-sm">
                      Your skills have been verified with {verificationScore}% score.
                      You now have a verified profile badge!
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-900 font-medium">
                        Resume Processed
                      </span>
                    </div>
                    <p className="text-blue-800 text-sm">
                      Your resume has been successfully parsed and analyzed by our
                      AI system.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={() => setShowResumeModal(true)}
                    className="w-full flex items-center justify-center px-4 py-3 bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View My Resume
                  </button>

                  {!isVerified && (
                    <>
                      <Tooltip content="Generate skill validation questions based on your resume">
                        <button
                          onClick={handleValidateSkills}
                          className="w-full flex items-center justify-center px-4 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Validate Your Skills
                        </button>
                      </Tooltip>
                      <p className="text-xs text-gray-500 text-center">
                        Generate 20 MCQ questions based on your resume data
                      </p>
                    </>
                  )}

                  {isVerified && (
                    <button
                      onClick={handleValidateSkills}
                      className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Retake Skills Test
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                    <span className="text-yellow-900 font-medium">
                      Resume Not Uploaded
                    </span>
                  </div>
                  <p className="text-yellow-800 text-sm">
                    Upload your resume to enable AI-powered skills validation
                    and get personalized test questions.
                  </p>
                </div>
                <div className="flex justify-center">
                  <Tooltip content="Upload your resume to enable AI-powered features">
                    <Link
                      to="/job-seeker/resume-upload"
                      className="w-full max-w-[800px] flex items-center justify-center px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors relative"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Resume
                    </Link>
                  </Tooltip>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Upload your resume to unlock skills validation
                </p>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Resume Text Modal */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">My Resume</h2>
              </div>
              <button
                onClick={() => setShowResumeModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {profileData?.resumeData?.rawResumeText ? (
                <div className="space-y-6">
                  {/* Personal Information */}
                  {profileData.resumeData.personalInfo && (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-lg border-l-4 border-red-600">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                        <UserIcon className="w-5 h-5 mr-2 text-red-600" />
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {profileData.resumeData.personalInfo.name && (
                          <div>
                            <span className="font-semibold text-gray-700">Name:</span>
                            <span className="ml-2 text-gray-900">{profileData.resumeData.personalInfo.name}</span>
                          </div>
                        )}
                        {profileData.resumeData.personalInfo.email && (
                          <div>
                            <span className="font-semibold text-gray-700">Email:</span>
                            <span className="ml-2 text-gray-900">{profileData.resumeData.personalInfo.email}</span>
                          </div>
                        )}
                        {profileData.resumeData.personalInfo.phone && (
                          <div>
                            <span className="font-semibold text-gray-700">Phone:</span>
                            <span className="ml-2 text-gray-900">{profileData.resumeData.personalInfo.phone}</span>
                          </div>
                        )}
                        {profileData.resumeData.personalInfo.location && (
                          <div>
                            <span className="font-semibold text-gray-700">Location:</span>
                            <span className="ml-2 text-gray-900">{profileData.resumeData.personalInfo.location}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Professional Summary */}
                  {profileData.resumeData.professionalInfo?.summary && (
                    <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-600">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-blue-600" />
                        Professional Summary
                      </h3>
                      <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                        {profileData.resumeData.professionalInfo.summary}
                      </p>
                    </div>
                  )}

                  {/* Skills */}
                  {profileData.resumeData.professionalInfo?.skills && profileData.resumeData.professionalInfo.skills.length > 0 && (
                    <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                        <Award className="w-5 h-5 mr-2 text-green-600" />
                        Skills
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {profileData.resumeData.professionalInfo.skills.map((skill: string, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-green-200 text-green-900 text-sm font-medium rounded-full border border-green-300"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work History */}
                  {profileData.resumeData.workHistory && profileData.resumeData.workHistory.length > 0 && (
                    <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-600">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
                        Work Experience
                      </h3>
                      <div className="space-y-4">
                        {profileData.resumeData.workHistory.map((job: any, index: number) => (
                          <div key={index} className="bg-white p-4 rounded-lg border border-purple-200">
                            <h4 className="font-bold text-gray-900 text-base">{job.title}</h4>
                            <p className="text-purple-700 font-semibold text-sm">{job.company}</p>
                            {job.duration && (
                              <p className="text-gray-600 text-xs mt-1">{job.duration}</p>
                            )}
                            {job.description && (
                              <p className="text-gray-700 text-sm mt-2 leading-relaxed whitespace-pre-wrap">
                                {job.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Education */}
                  {profileData.resumeData.education && (
                    <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-600">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                        <Award className="w-5 h-5 mr-2 text-yellow-600" />
                        Education
                      </h3>
                      <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                        {Array.isArray(profileData.resumeData.education)
                          ? profileData.resumeData.education
                              .map((edu: any) =>
                                `${edu.degree || 'Degree'} - ${edu.institution || 'Institution'} (${edu.year || 'Year'})`
                              )
                              .join('\n')
                          : profileData.resumeData.education}
                      </p>
                    </div>
                  )}

                  {/* Raw Resume Text (Collapsible) */}
                  <details className="bg-gray-50 p-6 rounded-lg border border-gray-300">
                    <summary className="cursor-pointer font-bold text-gray-900 flex items-center hover:text-red-600 transition-colors">
                      <FileText className="w-5 h-5 mr-2" />
                      View Raw Resume Text
                    </summary>
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <pre className="whitespace-pre-wrap font-mono text-xs text-gray-700 leading-relaxed">
                        {profileData.resumeData.rawResumeText}
                      </pre>
                    </div>
                  </details>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Resume Text Not Available
                  </h3>
                  <p className="text-gray-600">
                    The raw text from your resume could not be found. This might be because:
                  </p>
                  <ul className="text-gray-600 text-sm mt-2 space-y-1">
                    <li>• Your resume was uploaded before this feature was added</li>
                    <li>• The text extraction failed during upload</li>
                  </ul>
                  <p className="text-gray-600 mt-4">
                    Please try re-uploading your resume to view the text content.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowResumeModal(false)}
                className="w-full px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Modal */}
      {showFullLeaderboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Trophy className="w-6 h-6 text-yellow-600" />
                <h2 className="text-2xl font-bold text-gray-900">Leaderboard - Top Performers</h2>
              </div>
              <button
                onClick={() => setShowFullLeaderboard(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <Leaderboard
                userId={user?.id}
                period="weekly"
                limitCount={50}
                showCurrentUser={true}
                compact={false}
              />
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowFullLeaderboard(false)}
                className="w-full px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium"              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievements Grid (placed at bottom of page) */}
      {showAllAchievements && (
        <div className="mt-6">
          <AchievementGrid
            userAchievements={userAchievements}
            onAchievementClick={(achievement) => {
              setSelectedAchievement(achievement);
              setShowAchievementModal(true);
            }}
            showFilters={true}
            compact={false}
          />
        </div>
      )}

      {/* Achievement Modal */}
      {showAchievementModal && selectedAchievement && (
        <AchievementModal
          achievement={selectedAchievement}
          points={selectedAchievement.points}
          onClose={() => {
            setShowAchievementModal(false);
            setSelectedAchievement(null);
          }}
        />
      )}
    </div>
  );
};

export default Profile;
